import React from 'react';
import { useGameStore, formatNumber } from '../store/gameStore';

// CSS für den Slider
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: 2px solid #9333ea;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  .slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: 2px solid #9333ea;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  .slider:disabled::-webkit-slider-thumb {
    background: #6b7280;
    border-color: #6b7280;
    cursor: not-allowed;
  }
  
  .slider:disabled::-moz-range-thumb {
    background: #6b7280;
    border-color: #6b7280;
    cursor: not-allowed;
  }
`;

const PremiumAutoClicker: React.FC = () => {
  const { 
    gems, 
    premiumAutoClicker, 
    togglePremiumAutoClicker,
    setPremiumAutoClickerSpeed,
    playerLevel
  } = useGameStore();

  // Premium Auto-Klicker läuft jetzt auf App-Level - kein lokaler useEffect mehr nötig

  // Level-basierte Geschwindigkeitsbeschränkung
  const isLevel1000Plus = playerLevel >= 1000;
  const maxSpeed = isLevel1000Plus ? 2 : 25;
  const currentSpeed = Math.min(premiumAutoClicker.clicksPerSecond, maxSpeed);
  
  // Optimierte Kostenberechnung mit Rabatten
  const baseCostPerClick = 1000;
  const speedDiscount = Math.min(0.5, currentSpeed / 25); // Bis zu 50% Rabatt
  
  // Ab Level 1000: 1% aller Diamanten pro Klick (aktualisiert sich bei Diamanten-Änderungen)
  const costPerClick = React.useMemo(() => {
    return isLevel1000Plus 
      ? Math.floor(gems * 0.01) // 1% aller Diamanten
      : Math.floor(baseCostPerClick * (1 - speedDiscount)); // Normale Kosten
  }, [isLevel1000Plus, gems, speedDiscount]);
  
  const totalCostPerSecond = currentSpeed * costPerClick;
  const canAfford = gems >= totalCostPerSecond;
  const isActive = premiumAutoClicker.isActive;

  return (
    <>
      <style>{sliderStyles}</style>
      <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-xl p-3 sm:p-6 border-2 border-purple-400 shadow-lg">
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-3">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="text-2xl sm:text-3xl">⚡</div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">Auto-Klicker</h3>
            <p className="text-purple-200 text-xs sm:text-sm">Automatisches Klicken für Diamanten</p>
          </div>
        </div>
        
        <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${
          isActive 
            ? 'bg-green-500 text-white animate-pulse' 
            : 'bg-gray-600 text-gray-300'
        }`}>
          {isActive ? 'AKTIV' : 'INAKTIV'}
        </div>
      </div>

                {/* Geschwindigkeitseinstellung - nur für niedrige Level */}
                {!isLevel1000Plus && (
                  <div className="mb-4 sm:mb-6">
                    <div className="bg-black/30 rounded-lg p-3 sm:p-4">
                      <div className="text-purple-300 text-xs sm:text-sm mb-2 sm:mb-3">
                        Geschwindigkeit einstellen (1 - {maxSpeed} Klicks/s)
                      </div>
                      
                      {/* Slider für grobe Einstellung */}
                      <div className="mb-3 sm:mb-4">
                        <div className="text-purple-200 text-xs mb-2">Slider (linear)</div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                          <div className="flex-1">
                            <input
                              type="range"
                              min="1"
                              max={maxSpeed}
                              value={currentSpeed}
                              onChange={(e) => {
                                const newSpeed = Math.max(1, Math.min(maxSpeed, Number(e.target.value)));
                                setPremiumAutoClickerSpeed(newSpeed);
                              }}
                              className="w-full h-2 bg-purple-600 rounded-lg appearance-none cursor-pointer slider"
                              style={{
                                background: `linear-gradient(to right, #9333ea 0%, #9333ea ${(currentSpeed / maxSpeed) * 100}%, #4b5563 ${(currentSpeed / maxSpeed) * 100}%, #4b5563 100%)`
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Direkte Zahleneingabe */}
                      <div className="mb-3 sm:mb-4">
                        <div className="text-purple-200 text-xs mb-2">Exakte Eingabe</div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                          <input
                            type="number"
                            min="1"
                            max={maxSpeed}
                            value={currentSpeed}
                            onChange={(e) => setPremiumAutoClickerSpeed(Math.max(1, Math.min(maxSpeed, Number(e.target.value))))}
                            className="flex-1 bg-black/50 text-white rounded-lg px-2 sm:px-3 py-2 border border-purple-500 focus:border-purple-300 focus:outline-none text-sm"
                            placeholder={`1-${maxSpeed}`}
                          />
                          <div className="text-white font-bold text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">
                            {formatNumber(currentSpeed)} Klick{currentSpeed > 1 ? 's' : ''}/s
                          </div>
                        </div>
                      </div>

                      {/* Schnell-Buttons */}
                      <div className="mb-3 sm:mb-4">
                        <div className="text-purple-200 text-xs mb-2">Schnellauswahl</div>
                        <div className="grid grid-cols-5 gap-1 sm:gap-2">
                          {[1, 5, 10, 15, 25].filter(speed => speed <= maxSpeed).map(speed => (
                            <button
                              key={speed}
                              onClick={() => setPremiumAutoClickerSpeed(speed)}
                              className={`py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-bold transition-all duration-200 min-h-[36px] ${
                                currentSpeed === speed
                                  ? 'bg-purple-600 text-white border-2 border-purple-300'
                                  : 'bg-black/50 text-purple-300 border border-purple-600 hover:bg-purple-800 hover:text-white'
                              }`}
                            >
                              {formatNumber(speed)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {isActive && (
                        <div className="text-green-400 text-xs mt-2">
                          ✅ Geschwindigkeit kann jederzeit geändert werden
                        </div>
                      )}
                    </div>
                  </div>
                )}



      {/* Kompakte Anzeige für Level 1000+ */}
      {isLevel1000Plus ? (
        <div className="mb-4 sm:mb-6">
          <div className="bg-black/30 rounded-lg p-3 sm:p-4">
            <div className="text-blue-400 text-sm font-bold mb-2">
              ⚡ 2 Klicks für 1% aller Diamanten
            </div>
            <div className="text-purple-300 text-xs">
              💎 Verfügbare Diamanten: {formatNumber(gems)}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
          <div className="bg-black/30 rounded-lg p-2 sm:p-3">
            <div className="text-purple-300 text-xs sm:text-sm">Kosten pro Sekunde</div>
            <div className={`font-bold text-sm sm:text-base ${
              totalCostPerSecond >= 100000 ? 'text-red-400' : 
              totalCostPerSecond >= 10000 ? 'text-yellow-400' : 
              'text-white'
            }`}>
              💎 {formatNumber(totalCostPerSecond)}
              {totalCostPerSecond >= 100000 && <span className="text-xs ml-1">🔥</span>}
            </div>
            {speedDiscount > 0 && (
              <div className="text-green-400 text-xs mt-1">
                🎉 {Math.round(speedDiscount * 100)}% Rabatt aktiv!
              </div>
            )}
            {totalCostPerSecond >= 100000 && (
              <div className="text-red-300 text-xs mt-1">EXTREM TEUER!</div>
            )}
          </div>
          
          <div className="bg-black/30 rounded-lg p-2 sm:p-3">
            <div className="text-purple-300 text-xs sm:text-sm">Verfügbare Diamanten</div>
            <div className={`font-bold text-sm sm:text-base ${canAfford ? 'text-green-400' : 'text-red-400'}`}>
              💎 {formatNumber(gems)}
            </div>
            {gems > 0 && totalCostPerSecond > 0 && (
              <div className="text-purple-300 text-xs mt-1">
                Reicht für: {formatNumber(Math.floor(gems / totalCostPerSecond))}s
              </div>
            )}
          </div>
          
          {/* Performance-Anzeige */}
          <div className="bg-black/30 rounded-lg p-2 sm:p-3">
            <div className="text-purple-300 text-xs sm:text-sm">Performance-Modus</div>
            <div className="font-bold text-sm sm:text-base text-blue-400">
              {currentSpeed >= 20 ? '⚡ Schnell' :
               currentSpeed >= 10 ? '⚡ Mittel' : '⚡ Langsam'}
            </div>
            <div className="text-purple-300 text-xs mt-1">
              {currentSpeed >= 20 ? '200ms Updates' :
               currentSpeed >= 10 ? '500ms Updates' : '1s Updates'}
            </div>
          </div>
        </div>
      )}



      <div className="space-y-2 sm:space-y-3">
        <button
          onClick={togglePremiumAutoClicker}
          disabled={!isActive && !canAfford}
          className={`w-full py-3 px-4 sm:px-6 rounded-lg font-bold text-sm sm:text-lg transition-all duration-200 min-h-[44px] ${
            isActive
              ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-red-500/25'
              : canAfford
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-purple-500/25 transform hover:scale-105'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
          }`}
        >
          {isActive ? (
            <span className="flex items-center justify-center space-x-1 sm:space-x-2">
              <span>🛑</span>
              <span>Auto-Klicker INAKTIV</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-1 sm:space-x-2">
              <span>⚡</span>
              <span>Auto-Klicker AKTIV</span>
              {!isLevel1000Plus && (
                <span className="text-xs sm:text-sm opacity-75">
                  ({formatNumber(totalCostPerSecond)} 💎/s)
                </span>
              )}
            </span>
          )}
        </button>


      </div>



      {!canAfford && !isActive && (
        <div className="mt-3 sm:mt-4 bg-red-900/30 border border-red-500 rounded-lg p-2 sm:p-3">
          <div className="flex items-center space-x-2 text-red-400">
            <span>💎</span>
            <span className="font-bold text-sm">
              {isLevel1000Plus ? 'Nicht genug Diamanten!' : 'Nicht genug Diamanten!'}
            </span>
          </div>
          <div className="text-red-300 text-xs sm:text-sm mt-1">
            {isLevel1000Plus 
              ? 'Du benötigst mindestens 1% deiner Diamanten um den Auto-Klicker zu starten.'
              : `Du benötigst mindestens ${formatNumber(totalCostPerSecond)} 💎 um den Auto-Klicker zu starten.`
            }
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default PremiumAutoClicker; 